import React from 'react';
import { Row as TRow, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment-timezone';
import { ActionMemo, Approver } from '../../../api/actionMemoApiTypes';
import {
    AmStatusType,
    ApproverRoutingStatusType,
    DATE_TIME_FORMAT,
} from '../../../utils/Constants';
import { ProjectBalance } from '../../../api/projectBalanceApiTypes';
import { formatNameTitle, formatCurrency, formatDate } from '../../../utils/displayUtils';
import './Overview.css';

const renderOverdue = () => (
    <OverlayTrigger
        placement="right"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip id="tooltip">Approval is overdue!</Tooltip>}
    >
        <i className="fas fa-exclamation-triangle text-danger" />
    </OverlayTrigger>
);

type OverviewProps = {
    details: ActionMemo;
    balance: ProjectBalance | null;
    contractSigner?: Approver;
    approvedAm: boolean;
};

const Overview = (props: OverviewProps) => {
    const { details, balance, contractSigner, approvedAm } = props;

    const netAvailable = balance
        ? balance.originalAmount -
          balance.fundsCheckTotal -
          balance.commitmentTotal -
          balance.actualTotal
        : 0;

    const currentApprover =
        (details.amStatus === AmStatusType.Pending || details.amStatus === AmStatusType.Hold) &&
        details.approvers.find(
            (approver) => approver.routingStatus === ApproverRoutingStatusType.CURRENT,
        );

    return (
        <>
            <TRow className="mh-100">
                <Col xl="6" className="py-2 px-3">
                    <div className="border p-2 mb-2 h-100">
                        <div className="container-fluid mt-3">
                            <div className="row row-page-header border-bottom">
                                <div className="col-12 d-flex align-items-center mb-2">
                                    <h4 className="mr-auto text-weight-normal mb-0 p-0">
                                        General Information
                                    </h4>
                                </div>
                            </div>
                            <div>
                                <Col className="mr-1 py-3">
                                    <TRow>
                                        <Col sm="5" className="col-overview-label">
                                            Creator
                                        </Col>
                                        <Col sm="7" className="border-bottom p-0">
                                            {details.amCreatorName}
                                        </Col>
                                    </TRow>
                                    <TRow>
                                        <Col sm="5" className="col-overview-label">
                                            Created On
                                        </Col>
                                        <Col sm="7" className="border-bottom p-0">
                                            {formatDate(details.creationDate)}
                                        </Col>
                                    </TRow>
                                    <TRow>
                                        <Col sm="5" className="col-overview-label">
                                            Project Manager
                                        </Col>
                                        <Col sm="7" className="border-bottom p-0">
                                            {details.projectManagerName}
                                        </Col>
                                    </TRow>
                                    {details.dueDate && (
                                        <TRow>
                                            <Col sm="5" className="col-overview-label">
                                                Due On
                                            </Col>
                                            <Col sm="7" className="border-bottom p-0">
                                                {details.dueDate && (
                                                    <span>
                                                        {formatDate(details.dueDate)}{' '}
                                                        {moment(details.dueDate).isBefore(
                                                            moment().startOf('day'),
                                                        ) && renderOverdue()}
                                                    </span>
                                                )}
                                            </Col>
                                        </TRow>
                                    )}
                                    {details.approvalDate && (
                                        <TRow>
                                            <Col sm="5" className="col-overview-label">
                                                Approved On
                                            </Col>
                                            <Col sm="7" className="border-bottom p-0">
                                                {formatDate(details.approvalDate)}
                                            </Col>
                                        </TRow>
                                    )}
                                    <TRow>
                                        <Col sm="5" className="col-overview-label">
                                            Organization
                                        </Col>
                                        <Col sm="7" className="border-bottom p-0">
                                            {details.organizationName}
                                        </Col>
                                    </TRow>
                                    {details.nclDepartmentCode && (
                                        <TRow>
                                            <Col sm="5" className="col-overview-label">
                                                NCL Department
                                            </Col>
                                            <Col sm="7" className="border-bottom p-0">
                                                [{details.nclDepartmentCode}]
                                                {details.nclDepartmentDescription
                                                    ? ` ${details.nclDepartmentDescription}`
                                                    : ''}
                                            </Col>
                                        </TRow>
                                    )}
                                    {details.nclDevCode && (
                                        <TRow>
                                            <Col sm="5" className="col-overview-label">
                                                NCL Dev Code
                                            </Col>
                                            <Col sm="7" className="border-bottom p-0">
                                                [{details.nclDevCode}]
                                                {details.nclDevCodeDescription
                                                    ? ` ${details.nclDevCodeDescription}`
                                                    : ''}
                                            </Col>
                                        </TRow>
                                    )}
                                    {details.completionDate && (
                                        <TRow>
                                            <Col sm="5" className="col-overview-label">
                                                Completion Date
                                            </Col>
                                            <Col sm="7" className="border-bottom p-0">
                                                {formatDate(details.completionDate)}
                                            </Col>
                                        </TRow>
                                    )}
                                    {details.supNumber > 0 &&
                                        details.newCompletionDate &&
                                        (details.amStatus === AmStatusType.Draft ||
                                            details.amStatus === AmStatusType.Pending ||
                                            details.amStatus === AmStatusType.Hold) && (
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    New Completion Date{' '}
                                                    <OverlayTrigger
                                                        placement="right"
                                                        delay={{ show: 250, hide: 400 }}
                                                        overlay={
                                                            <Tooltip id="tooltip">
                                                                Replaces the completion date when
                                                                supplement is approved.
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i className="fas fa-question-circle tooltip-color" />
                                                    </OverlayTrigger>
                                                </Col>
                                                <Col sm="7" className="border-bottom p-0">
                                                    {formatDate(details.newCompletionDate)}
                                                </Col>
                                            </TRow>
                                        )}
                                    {contractSigner && (
                                        <>
                                            {details.contractOrganizationName && (
                                                <TRow>
                                                    <Col sm="5" className="col-overview-label">
                                                        Contract Entity
                                                    </Col>
                                                    <Col sm="7" className="border-bottom p-0">
                                                        {details.contractOrganizationName}
                                                    </Col>
                                                </TRow>
                                            )}
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Contract Signer
                                                </Col>
                                                <Col sm="7" className="border-bottom p-0">
                                                    {formatNameTitle(
                                                        contractSigner.approverName,
                                                        contractSigner.approverTitle,
                                                    )}
                                                </Col>
                                            </TRow>
                                        </>
                                    )}
                                    {currentApprover && (
                                        <TRow>
                                            <Col sm="5" className="col-overview-label">
                                                Current Approver
                                            </Col>
                                            <Col sm="7" className="border-bottom p-0">
                                                {formatNameTitle(
                                                    currentApprover.approverName,
                                                    currentApprover.approverTitle,
                                                )}
                                            </Col>
                                        </TRow>
                                    )}
                                    <TRow>
                                        <Col sm="5" className="col-overview-label">
                                            Confidential
                                        </Col>
                                        <Col sm="7" className="border-bottom p-0">
                                            {details.confidential ? (
                                                <span
                                                    className={`badge ${
                                                        details.confidential
                                                            ? ' text-dark badge-warning '
                                                            : ' text-white badge-success'
                                                    }`}
                                                >
                                                    YES
                                                </span>
                                            ) : (
                                                'No'
                                            )}
                                        </Col>
                                    </TRow>
                                </Col>
                            </div>
                            {details.statusChangeRequest && (
                                <>
                                    <div className="row row-page-header border-bottom mt-3">
                                        <div className="col-12 d-flex align-items-center mb-2">
                                            <h4 className="mr-auto text-weight-normal mb-0 p-0">
                                                Status Change Request
                                            </h4>
                                        </div>
                                    </div>
                                    <div>
                                        <Col className="mr-1 py-3">
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Requested By
                                                </Col>
                                                <Col sm="7" className="border-bottom p-0">
                                                    {details.statusChangeRequest.requestedByName}
                                                </Col>
                                            </TRow>
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Requested On
                                                </Col>
                                                <Col sm="7" className="border-bottom p-0">
                                                    {formatDate(
                                                        details.statusChangeRequest.requestedOn,
                                                        DATE_TIME_FORMAT,
                                                    )}
                                                </Col>
                                            </TRow>
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Requested Status
                                                </Col>
                                                <Col sm="7" className="border-bottom p-0">
                                                    {details.statusChangeRequest.requestedStatus}
                                                </Col>
                                            </TRow>
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Comment
                                                </Col>
                                                <Col sm="7" className="border-bottom p-0">
                                                    {details.statusChangeRequest.commentMessage}
                                                </Col>
                                            </TRow>
                                        </Col>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Col>
                <Col xl="6" className="py-2 px-3">
                    <div className="border p-2 mb-2 h-100">
                        <div className="container-fluid mt-3">
                            <div className="row row-page-header border-bottom">
                                <div className="col-12 d-flex align-items-center mb-2">
                                    <h4 className="mr-auto text-weight-normal mb-0 p-0">Funds</h4>
                                </div>
                            </div>
                            <div>
                                {details.supNumber <= 0 ? (
                                    <Col className="mr-1 py-3">
                                        {(details.template.showBudgetAmount ||
                                            !!details.budgetAmount) && (
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Budget Amount
                                                </Col>
                                                <Col sm="7" className="text-right">
                                                    <div className="border-bottom">
                                                        {formatCurrency(details.budgetAmount)}
                                                    </div>
                                                </Col>
                                            </TRow>
                                        )}
                                        {(details.template.showInternalCostAmount ||
                                            !!details.internalCostAmount) && (
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Internal Cost
                                                </Col>
                                                <Col sm="7" className="text-right">
                                                    <div className="border-bottom">
                                                        {formatCurrency(details.internalCostAmount)}
                                                    </div>
                                                </Col>
                                            </TRow>
                                        )}
                                        {(details.template.showRevenueAmount ||
                                            !!details.revenueAmount) && (
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Expected Revenue
                                                </Col>
                                                <Col sm="7" className="text-right">
                                                    <div className="border-bottom">
                                                        {formatCurrency(details.revenueAmount)}
                                                    </div>
                                                </Col>
                                            </TRow>
                                        )}
                                        {details.template.showBudgetAmount ||
                                        details.template.showInternalCostAmount ||
                                        details.template.showRevenueAmount ||
                                        !!details.budgetAmount ||
                                        !!details.internalCostAmount ||
                                        !!details.revenueAmount ? (
                                            <TRow>
                                                <Col sm="5" className="col-overview-label">
                                                    Total
                                                </Col>
                                                <Col sm="7" className="text-right">
                                                    <div className="border-bottom">
                                                        {formatCurrency(
                                                            details.budgetAmount +
                                                                details.internalCostAmount +
                                                                details.revenueAmount,
                                                        )}
                                                    </div>
                                                </Col>
                                            </TRow>
                                        ) : (
                                            <TRow>
                                                <Col sm="12">No budget allowed</Col>
                                            </TRow>
                                        )}
                                    </Col>
                                ) : (
                                    <div className="scrollXContainer">
                                        <div className="fundsContainer">
                                            <Col className="mr-1 py-3">
                                                <TRow>
                                                    <Col xs="3"> </Col>
                                                    <Col xs="3" className="text-center">
                                                        <div className="boxed">Supplement</div>
                                                    </Col>
                                                    <Col xs="3" className="text-center">
                                                        <div className="boxed">
                                                            Original & Prior
                                                        </div>
                                                    </Col>
                                                    <Col xs="3" className="text-center">
                                                        <div className="boxed">Total</div>
                                                    </Col>
                                                </TRow>
                                                {(details.template.showBudgetAmount ||
                                                    !!details.budgetAmount ||
                                                    !!details.budgetTotal) && (
                                                    <TRow>
                                                        <Col xs="3">Budget Amount</Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.budgetAmount,
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.budgetTotal -
                                                                        details.budgetAmount,
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.budgetTotal,
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </TRow>
                                                )}
                                                {(details.template.showInternalCostAmount ||
                                                    !!details.internalCostAmount ||
                                                    !!details.internalCostTotal) && (
                                                    <TRow>
                                                        <Col xs="3">Internal Cost</Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.internalCostAmount,
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.internalCostTotal -
                                                                        details.internalCostAmount,
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.internalCostTotal,
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </TRow>
                                                )}
                                                {(details.template.showRevenueAmount ||
                                                    !!details.revenueAmount ||
                                                    !!details.revenueTotal) && (
                                                    <TRow>
                                                        <Col xs="3">Expected Revenue</Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.revenueAmount,
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.revenueTotal -
                                                                        details.revenueAmount,
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.revenueTotal,
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </TRow>
                                                )}
                                                {details.template.showBudgetAmount ||
                                                details.template.showInternalCostAmount ||
                                                details.template.showRevenueAmount ||
                                                !!details.budgetAmount ||
                                                !!details.budgetTotal ||
                                                !!details.internalCostAmount ||
                                                !!details.internalCostTotal ||
                                                !!details.revenueAmount ||
                                                !!details.revenueTotal ? (
                                                    <TRow>
                                                        <Col xs="3">Total</Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.budgetAmount +
                                                                        details.internalCostAmount +
                                                                        details.revenueAmount,
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.budgetTotal +
                                                                        details.internalCostTotal +
                                                                        details.revenueTotal -
                                                                        (details.budgetAmount +
                                                                            details.internalCostAmount +
                                                                            details.revenueAmount),
                                                                )}
                                                            </div>
                                                        </Col>
                                                        <Col xs="3" className="text-right">
                                                            <div className="border-bottom">
                                                                {formatCurrency(
                                                                    details.budgetTotal +
                                                                        details.internalCostTotal +
                                                                        details.revenueTotal,
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </TRow>
                                                ) : (
                                                    <TRow>
                                                        <Col xs="12">No budget allowed</Col>
                                                    </TRow>
                                                )}
                                            </Col>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {approvedAm && balance && (
                                <>
                                    <div className="row row-page-header border-bottom mt-3">
                                        <div className="col-12 d-flex align-items-center mb-2">
                                            <h4 className="mr-auto text-weight-normal mb-0 p-0">
                                                Project Balances
                                            </h4>
                                        </div>
                                    </div>
                                    <div>
                                        <Col className="mr-1 pt-3">
                                            <TRow>
                                                <Col sm={details.supNumber <= 0 ? 5 : 3}>
                                                    Budget + All Supplements
                                                </Col>
                                                <Col
                                                    sm={details.supNumber <= 0 ? 7 : 9}
                                                    className="text-right"
                                                >
                                                    <div className="border-bottom">
                                                        {formatCurrency(balance.originalAmount)}
                                                    </div>
                                                </Col>
                                            </TRow>
                                            <TRow>
                                                <Col sm={details.supNumber <= 0 ? 5 : 3}>
                                                    Pending Commitments
                                                </Col>
                                                <Col
                                                    sm={details.supNumber <= 0 ? 7 : 9}
                                                    className="text-right"
                                                >
                                                    <div className="border-bottom">
                                                        {formatCurrency(balance.fundsCheckTotal)}
                                                    </div>
                                                </Col>
                                            </TRow>
                                            <TRow>
                                                <Col sm={details.supNumber <= 0 ? 5 : 3}>
                                                    Commitments
                                                </Col>
                                                <Col
                                                    sm={details.supNumber <= 0 ? 7 : 9}
                                                    className="text-right"
                                                >
                                                    <div className="border-bottom">
                                                        {formatCurrency(balance.commitmentTotal)}
                                                    </div>
                                                </Col>
                                            </TRow>
                                            <TRow>
                                                <Col sm={details.supNumber <= 0 ? 5 : 3}>
                                                    Actuals
                                                </Col>
                                                <Col
                                                    sm={details.supNumber <= 0 ? 7 : 9}
                                                    className="text-right"
                                                >
                                                    <div className="border-bottom">
                                                        {formatCurrency(balance.actualTotal)}
                                                    </div>
                                                </Col>
                                            </TRow>
                                            <TRow className="mt-3 py-3 bold border bg-white">
                                                <Col sm={details.supNumber <= 0 ? 5 : 3}>
                                                    <h3 className="responsive-total">
                                                        Net Available
                                                    </h3>
                                                </Col>
                                                <Col
                                                    sm={details.supNumber <= 0 ? 7 : 9}
                                                    className="pl-0 text-right"
                                                >
                                                    <h3 className="responsive-total">
                                                        {formatCurrency(netAvailable)}
                                                    </h3>
                                                </Col>
                                            </TRow>
                                        </Col>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Col>
            </TRow>
            <TRow className="mh-100">
                <Col xl="6" className="py-2 px-3">
                    <div className="border p-2 h-100">
                        <div className="container-fluid mt-3">
                            <div className="row row-page-header border-bottom">
                                <div className="col-12 d-flex align-items-center mb-2">
                                    <h4 className="mr-auto text-weight-normal mb-0 p-0">
                                        Action Requested
                                    </h4>
                                </div>
                            </div>
                            <div className="py-3">
                                <textarea
                                    aria-label="Action Requested"
                                    className="form-control customTextArea"
                                    name="actionRequested"
                                    rows={5}
                                    value={details.actionRequested ? details.actionRequested : ''}
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>
                </Col>
                <Col xl="6" className="py-2 px-3">
                    <div className="border p-2 h-100">
                        <div className="container-fluid mt-3">
                            <div className="row row-page-header border-bottom">
                                <div className="col-12 d-flex align-items-center mb-2">
                                    <h4 className="mr-auto text-weight-normal mb-0 p-0">
                                        Justification
                                    </h4>
                                </div>
                            </div>
                            <div className="py-3">
                                <textarea
                                    aria-label="Justification"
                                    className="form-control customTextArea"
                                    name="justification"
                                    value={details.justification ? details.justification : ''}
                                    rows={5}
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>
                </Col>
            </TRow>
        </>
    );
};

export default Overview;
